import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = { isLoading: false, data: {}, errors: [], myServers: [], myServersLoading: false };

export const getServers = createAsyncThunk('servers/getServers', async (args, thunkApi) => {
  try {
    const { page = 1, size = 10, query = '', filter = JSON.stringify({}), sort = '' } = args || {};
    const sortParam = sort ? `&sort=${encodeURIComponent(sort)}` : '';
    const { data } = await axios.get(
      `/api/get_servers?page=${page}&size=${size}&query=${query}&filter=${filter}${sortParam}`
    );
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const createServer = createAsyncThunk('servers/createServer', async (args, thunkApi) => {
  try {
    const { data } = await axios.post('/api/create_server', args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const updateServer = createAsyncThunk('servers/updateServer', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/update_server/${args.get ? args.get('_id') : args._id}`, args, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const deleteServer = createAsyncThunk('servers/deleteServer', async (args, thunkApi) => {
  try {
    const { data } = await axios.delete(`/api/delete_server/${args._id}`, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const voteServer = createAsyncThunk('servers/voteServer', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/vote_server/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token },
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.data || { errors: [{ msg: 'something went wrong' }] }
    );
  }
});

export const viewServer = createAsyncThunk('servers/viewServer', async (serverId, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/view_server/${serverId}`);
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue({ errors: [{ msg: 'something went wrong' }] });
  }
});

export const approveServer = createAsyncThunk('servers/approveServer', async (args, thunkApi) => {
  try {
    const { data } = await axios.put(`/api/approve_server/${args._id}`, {}, {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

export const getMyServers = createAsyncThunk('servers/getMyServers', async (_, thunkApi) => {
  try {
    const { data } = await axios.get('/api/get_my_servers', {
      headers: { Authorization: thunkApi.getState().auth.token }
    });
    return thunkApi.fulfillWithValue(data);
  } catch (errors) {
    return thunkApi.rejectWithValue(
      errors.response?.status !== 400 ? { errors: [{ msg: 'something went wrong' }] } : errors.response.data
    );
  }
});

const serversSlice = createSlice({
  name: 'servers',
  initialState,
  extraReducers: {
    [getServers.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [getServers.fulfilled]: (state, action) => { state.isLoading = false; state.data = action.payload; },
    [getServers.rejected]: (state, action) => { state.isLoading = false; state.data = {}; state.errors = action.payload.errors; },

    [createServer.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [createServer.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.itemsCount = (state.data.itemsCount || 0) + 1;
      if (state.data.data?.length < 10) state.data.data = [...(state.data.data || []), action.payload.data];
    },
    [createServer.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [updateServer.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [updateServer.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [updateServer.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [deleteServer.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [deleteServer.fulfilled]: (state, action) => {
      state.isLoading = false;
      state.data.data = state.data.data?.filter(el => el._id !== action.payload.data._id);
      state.data.itemsCount = Math.max(0, (state.data.itemsCount || 1) - 1);
    },
    [deleteServer.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [voteServer.fulfilled]: (state, action) => {
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },

    [approveServer.pending]: (state) => { state.isLoading = true; state.errors = []; },
    [approveServer.fulfilled]: (state, action) => {
      state.isLoading = false;
      const idx = state.data.data?.findIndex(el => el._id === action.payload.data._id);
      if (idx !== undefined && idx >= 0) state.data.data[idx] = action.payload.data;
    },
    [approveServer.rejected]: (state, action) => { state.isLoading = false; state.errors = action.payload.errors; },

    [getMyServers.pending]: (state) => { state.myServersLoading = true; state.myServers = []; },
    [getMyServers.fulfilled]: (state, action) => { state.myServersLoading = false; state.myServers = action.payload.data; },
    [getMyServers.rejected]: (state) => { state.myServersLoading = false; },
  }
});

export default serversSlice.reducer;
